import type { Area } from "react-easy-crop";

/**
 * Image optimization options
 */
export interface ImageOptimizationOptions {
  /** Maximum width in pixels (default: 2048) */
  maxWidth?: number;
  /** Maximum height in pixels (default: 2048) */
  maxHeight?: number;
  /** Quality for lossy formats 0-1 (default: 0.85) */
  quality?: number;
  /** Output format (default: webp) */
  format?: "webp" | "jpeg" | "png";
  /** Target maximum output size in MB. Lossy formats will reduce quality/dimensions until under this size. */
  maxSizeMB?: number;
}

const DEFAULT_OPTIONS: Required<ImageOptimizationOptions> = {
  maxWidth: 2048,
  maxHeight: 2048,
  quality: 0.88,
  format: "webp",
  maxSizeMB: 5,
};

const MIN_LOSSY_QUALITY = 0.72;
const MIN_OUTPUT_DIMENSION = 1200;

/**
 * Creates a new Image element from a URL
 */
export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

/**
 * Converts degrees to radians
 */
export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateResizedDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  // If already within limits, return original dimensions
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const aspectRatio = width / height;

  let newWidth = width;
  let newHeight = height;

  // Scale down to fit within maxWidth
  if (newWidth > maxWidth) {
    newWidth = maxWidth;
    newHeight = newWidth / aspectRatio;
  }

  // Scale down to fit within maxHeight
  if (newHeight > maxHeight) {
    newHeight = maxHeight;
    newWidth = newHeight * aspectRatio;
  }

  return {
    width: Math.round(newWidth),
    height: Math.round(newHeight),
  };
}

/**
 * Get MIME type from format
 */
function getMimeType(format: "webp" | "jpeg" | "png"): string {
  const mimeTypes = {
    webp: "image/webp",
    jpeg: "image/jpeg",
    png: "image/png",
  };
  return mimeTypes[format];
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

function drawResizedCanvas(
  sourceCanvas: HTMLCanvasElement,
  width: number,
  height: number,
): HTMLCanvasElement | null {
  const resizedCanvas = document.createElement("canvas");
  const resizedCtx = resizedCanvas.getContext("2d");

  if (!resizedCtx) {
    return null;
  }

  resizedCanvas.width = width;
  resizedCanvas.height = height;
  resizedCtx.imageSmoothingEnabled = true;
  resizedCtx.imageSmoothingQuality = "high";
  resizedCtx.drawImage(sourceCanvas, 0, 0, width, height);

  return resizedCanvas;
}

async function exportCanvasWithTargetSize(
  canvas: HTMLCanvasElement,
  options: Required<ImageOptimizationOptions>,
): Promise<Blob | null> {
  const mimeType = getMimeType(options.format);
  const maxBytes = options.maxSizeMB * 1024 * 1024;

  if (options.format === "png") {
    return canvasToBlob(canvas, mimeType, options.quality);
  }

  let currentCanvas = canvas;
  let quality = options.quality;

  for (let resizeAttempt = 0; resizeAttempt < 4; resizeAttempt++) {
    for (let qualityAttempt = 0; qualityAttempt < 7; qualityAttempt++) {
      const blob = await canvasToBlob(currentCanvas, mimeType, quality);

      if (!blob) {
        return null;
      }

      if (blob.size <= maxBytes) {
        return blob;
      }

      if (quality <= MIN_LOSSY_QUALITY) {
        break;
      }

      quality = Math.max(MIN_LOSSY_QUALITY, quality - 0.04);
    }

    const resizedWidth = Math.max(
      MIN_OUTPUT_DIMENSION,
      Math.round(currentCanvas.width * 0.85),
    );
    const resizedHeight = Math.max(
      MIN_OUTPUT_DIMENSION,
      Math.round(currentCanvas.height * 0.85),
    );

    if (
      resizedWidth === currentCanvas.width &&
      resizedHeight === currentCanvas.height
    ) {
      break;
    }

    const resizedCanvas = drawResizedCanvas(
      currentCanvas,
      resizedWidth,
      resizedHeight,
    );

    if (!resizedCanvas) {
      return null;
    }

    currentCanvas = resizedCanvas;
    quality = options.quality;
  }

  const blob = await canvasToBlob(currentCanvas, mimeType, MIN_LOSSY_QUALITY);
  return blob && blob.size <= maxBytes ? blob : null;
}

/**
 * Get file extension from format
 */
export function getExtension(format: "webp" | "jpeg" | "png"): string {
  return format === "jpeg" ? "jpg" : format;
}

/**
 * Crops, optimizes, and converts an image using canvas
 * @param imageSrc - Source URL of the image
 * @param pixelCrop - Crop area in pixels from react-easy-crop
 * @param rotation - Rotation angle in degrees (default: 0)
 * @param flip - Flip transformations (default: no flip)
 * @param options - Optimization options (maxWidth, maxHeight, quality, format)
 * @returns Promise<Blob | null>
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
  options: ImageOptimizationOptions = {},
): Promise<Blob | null> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // Calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  // Calculate resized dimensions for the cropped area
  const { width: finalWidth, height: finalHeight } = calculateResizedDimensions(
    pixelCrop.width,
    pixelCrop.height,
    opts.maxWidth,
    opts.maxHeight,
  );

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas to the final resized dimensions
  croppedCanvas.width = finalWidth;
  croppedCanvas.height = finalHeight;

  // Enable image smoothing for better quality when resizing
  croppedCtx.imageSmoothingEnabled = true;
  croppedCtx.imageSmoothingQuality = "high";

  // Draw the cropped and resized image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    finalWidth,
    finalHeight,
  );

  return exportCanvasWithTargetSize(croppedCanvas, opts);
}

/**
 * Converts a Blob to a File object
 * @param blob - The blob to convert
 * @param fileName - Name for the file (extension will be updated to match format)
 * @param format - Output format (default: webp)
 * @returns File
 */
export function blobToFile(
  blob: Blob,
  fileName: string,
  format: "webp" | "jpeg" | "png" = "webp",
): File {
  const mimeType = getMimeType(format);
  const extension = getExtension(format);

  // Update filename extension to match the actual format
  const baseName = fileName.replace(/\.[^/.]+$/, "");
  const newFileName = `${baseName}.${extension}`;

  return new File([blob], newFileName, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

/**
 * Gets a data URL from a file
 * @param file - File to read
 * @returns Promise<string>
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Optimize an image without cropping (for use with already-cropped images)
 * @param imageSrc - Source URL or data URL of the image
 * @param options - Optimization options
 * @returns Promise<Blob | null>
 */
export async function optimizeImage(
  imageSrc: string,
  options: ImageOptimizationOptions = {},
): Promise<Blob | null> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const image = await createImage(imageSrc);

  const { width: finalWidth, height: finalHeight } = calculateResizedDimensions(
    image.width,
    image.height,
    opts.maxWidth,
    opts.maxHeight,
  );

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  canvas.width = finalWidth;
  canvas.height = finalHeight;

  // Enable image smoothing for better quality when resizing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Draw the resized image
  ctx.drawImage(image, 0, 0, finalWidth, finalHeight);

  return exportCanvasWithTargetSize(canvas, opts);
}
